import axios from 'axios';

// Configurable via REACT_APP_API_URL (CRA env var); falls back to local dev backend
export const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

// Create axios instance with base configuration
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 1800000, // 30 minutes timeout for long-running AI operations
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const isAuthRequest = error.config?.url?.includes('/auth/signin') ||
      error.config?.url?.includes('/auth/signup');

    // Don't force-redirect when the sign-in attempt itself fails â€”
    // that reload would hide the "Invalid credentials" error message
    if (error.response?.status === 401 && !isAuthRequest) {
      // Token expired or invalid
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      if (window.location.pathname !== '/signin') {
        window.location.href = '/signin';
      }
    }
    return Promise.reject(error);
  }
);

// Auth API calls
export const authAPI = {
  // Sign up
  signUp: async (userData) => {
    const response = await api.post('/auth/signup', userData);
    return response.data;
  },

  // Sign in
  signIn: async (credentials) => {
    const response = await api.post('/auth/signin', credentials);
    return response.data;
  },

  // Get current user profile
  getProfile: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  },

  // Public: is self-serve signup open? Drives whether the sign-in page offers
  // a "create account" link. The server enforces it regardless.
  config: async () => {
    const response = await api.get('/auth/config');
    return response.data;
  },

  // Consume the emailed token. Returns { token, user } — verifying signs you in.
  verifyEmail: async (token) => {
    const response = await api.post('/auth/verify-email', { token });
    return response.data;
  },

  // Always resolves the same way whether or not the address exists.
  resendVerification: async (email) => {
    const response = await api.post('/auth/resend-verification', { email });
    return response.data;
  }
};

// Billing / subscription API calls (the signed-in user, about themselves)
export const billingAPI = {
  // { state, allowed, planName, trialEndsAt, currentPeriodEnd, usage, trial }
  me: async () => {
    const response = await api.get('/billing/me');
    return response.data;
  },

  // Buyable plans only (active, price in rupees).
  // Returns { plans, imageMessageCost } — the cost is org-wide, not per-plan.
  myPlans: async () => {
    const response = await api.get('/billing/my-plans');
    return { plans: response.data.plans, imageMessageCost: response.data.imageMessageCost };
  },

  // Returns a Razorpay Payment Link URL to send the user to
  checkout: async (planId) => {
    const response = await api.post('/billing/checkout', { planId });
    return response.data;
  },

  // Buy prepaid messages. Returns { shortUrl, messages }.
  buyCredits: async (amountINR) => {
    const response = await api.post('/billing/credits/checkout', { amountINR });
    return response.data;
  },

  // Ask the server to check this payment link with Razorpay directly and settle
  // the account if it's paid. The backup for a webhook that never arrives.
  // Returns { paid, applied }. Safe to call repeatedly — applying is
  // exactly-once server-side.
  verifyPayment: async (linkId) => {
    const response = await api.post('/billing/verify-payment', { linkId });
    return response.data;
  }
};

// A user's OWN integrations — their connectors and MCP servers.
// Tokens are write-only: the server returns hasToken, never the secret.
export const integrationsAPI = {
  // { mcp: bool, connectors: bool } — plan + admin override already resolved
  features: async () => {
    const response = await api.get('/my/features');
    return response.data.features;
  },

  getConnectors: async () => {
    const response = await api.get('/my/connectors');
    return response.data.connectors;
  },
  saveConnectors: async (connectors) => {
    const response = await api.put('/my/connectors', { connectors });
    return response.data.connectors;
  },

  getMcpServers: async () => {
    const response = await api.get('/my/mcp-servers');
    return response.data.servers;
  },
  saveMcpServers: async (servers) => {
    const response = await api.put('/my/mcp-servers', { servers });
    return response.data.servers;
  }
};

// Retry configuration for API calls
const RETRY_CONFIG = {
  maxRetries: 3,
  retryDelay: 1000, // 1 second
  retryCondition: (error) => {
    // Retry on network errors or 5xx server errors
    return !error.response || (error.response.status >= 500 && error.response.status < 600);
  }
};

// Helper function for retry logic
const retryWithBackoff = async (apiCall, config = RETRY_CONFIG) => {
  let lastError;

  for (let attempt = 0; attempt <= config.maxRetries; attempt++) {
    try {
      return await apiCall();
    } catch (error) {
      lastError = error;

      // Check if we should retry
      if (attempt === config.maxRetries || !config.retryCondition(error)) {
        break;
      }

      // Wait before retrying (exponential backoff)
      const delay = config.retryDelay * Math.pow(2, attempt);
      console.log(`API call failed, retrying in ${delay}ms (attempt ${attempt + 1}/${config.maxRetries})`);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }

  throw lastError;
};

// Chat API calls
export const chatAPI = {
  sendMessage: async (message, selectedWebhook = 1) => {
    return retryWithBackoff(async () => {
      try {
        // Use the backend chat API which handles individual user webhook URLs
        const response = await api.post('/chat', {
          text: message,
          selectedWebhook: selectedWebhook
        });

        // Check if response contains binary data (image, audio, etc.)
        if (response.data.binaryData) {
          console.log('Binary data received:', {
            contentType: response.data.contentType,
            binaryDataLength: response.data.binaryData.length,
            isDataUrl: response.data.binaryData.startsWith('data:'),
            dataUrlStart: response.data.binaryData.substring(0, 50)
          });

          // Validate the data URL format
          if (!response.data.binaryData.startsWith('data:')) {
            console.error('Invalid data URL format:', response.data.binaryData.substring(0, 100));
            throw new Error('Invalid binary data format received from server');
          }

          // Try to convert data URL to blob URL for better performance
          let blobUrl = response.data.binaryData;

          try {
            // Extract base64 data from data URL
            const base64Data = response.data.binaryData.split(',')[1];
            const binaryData = atob(base64Data);
            const bytes = new Uint8Array(binaryData.length);
            for (let i = 0; i < binaryData.length; i++) {
              bytes[i] = binaryData.charCodeAt(i);
            }
            const blob = new Blob([bytes], { type: response.data.contentType });
            blobUrl = URL.createObjectURL(blob);
            console.log('Converted data URL to blob URL:', blobUrl);

            // Note: We do NOT automatically revoke the URL here anymore.
            // It will be handled by the browser's page lifecycle or explicit cleanup if needed.
          } catch (error) {
            console.warn('Failed to convert data URL to blob, using original data URL:', error.message);
          }

          return {
            success: true,
            response: response.data.reply,
            binaryData: blobUrl,
            contentType: response.data.contentType,
            data: response.data,
            // Ensure the blob URL is returned for chat display
            _blobUrl: blobUrl
          };
        }

        return {
          success: true,
          response: response.data.reply,
          data: response.data
        };
      } catch (error) {
        console.error('Chat API error:', error);
        throw error;
      }
    });
  },


  // Send message directly to the configured AI provider (Claude / OpenAI / Gemini / Ollama)
  sendMessageToAI: async (message, history = []) => {
    const response = await api.post('/chat/ai', {
      text: message,
      history
    });
    return {
      success: true,
      response: response.data.reply,
      data: response.data
    };
  },

  // Stream a reply from the AI provider token-by-token (Server-Sent Events).
  // onChunk(fullTextSoFar) is called as the reply grows. Returns the full text.
  // Pass an AbortSignal to support "stop generation", and images for vision.
  streamMessageToAI: async (message, history = [], onChunk, signal, images = []) => {
    const token = localStorage.getItem('token');
    const response = await fetch(`${API_BASE_URL}/chat/ai/stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      },
      body: JSON.stringify({ text: message, history, images }),
      signal
    });

    // Non-SSE response means a validation/config error â€” surface its message
    const contentType = response.headers.get('content-type') || '';

    // A successful JSON reply is not an error: some turns have nothing to
    // stream. A generated image arrives whole, so the server answers as plain
    // JSON instead of switching to SSE. Deliver it through the same onChunk
    // the caller already uses, and hand the binary back for rendering.
    if (response.ok && contentType.includes('application/json')) {
      const data = await response.json();
      const replyText = data.reply || '';
      if (replyText && onChunk) onChunk(replyText);
      return { text: replyText, binaryData: data.binaryData, contentType: data.contentType };
    }

    if (!response.ok || !contentType.includes('text/event-stream')) {
      let errMessage = 'Failed to send message. Please try again.';
      let errCode = '';
      let errScope = '';
      try {
        const data = await response.json();
        errMessage = data.message || errMessage;
        errCode = data.code || '';
        errScope = data.scope || ''; // 'daily' | 'monthly' on a 429
      } catch { /* not JSON */ }

      // This fetch bypasses the axios interceptor, so handle an expired or
      // revoked session here too (e.g. after an admin password reset).
      // Without this the user just sees the error on every message with no
      // way back to the sign-in page.
      if (response.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        if (window.location.pathname !== '/signin') {
          window.location.href = '/signin';
        }
      }
      // Carry the status/code through — this fetch bypasses axios, so without
      // them the caller can't tell "trial over, show plans" from a generic
      // failure and would just print the text in a chat bubble.
      const err = new Error(errMessage);
      err.status = response.status;
      err.code = errCode;
      err.scope = errScope;
      throw err;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let fullText = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      // SSE events are separated by a blank line
      const events = buffer.split('\n\n');
      buffer = events.pop(); // keep any incomplete event in the buffer

      for (const event of events) {
        const dataLine = event.split('\n').find(l => l.startsWith('data: '));
        if (!dataLine) continue;

        let payload;
        try {
          payload = JSON.parse(dataLine.slice(6));
        } catch {
          continue;
        }

        if (payload.error) {
          throw new Error(payload.error);
        }
        if (payload.delta) {
          fullText += payload.delta;
          if (onChunk) onChunk(fullText);
        }
        // payload.done => stream complete (loop exits on reader done)
      }
    }

    // Same shape as the JSON branch above, so callers have one thing to read.
    return { text: fullText };
  },

  // Settings API calls
  getSettings: async () => {
    try {
      const response = await api.get('/v2/admin/me/settings');
      return response.data.settings;
    } catch (error) {
      console.error('Get settings error:', error);
      throw error;
    }
  },

  updateSettings: async (settings) => {
    try {
      const response = await api.put('/v2/admin/me/settings', settings);
      return response.data;
    } catch (error) {
      console.error('Update settings error:', error);
      throw error;
    }
  },

  // Send message with file attachment (direct binary forwarding)
  sendMessageWithFile: async (formData) => {
    try {
      const response = await api.post('/chat/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        }
      });

      console.log('File upload response received:', {
        reply: response.data.reply,
        binaryData: response.data.binaryData ? 'present' : 'absent'
      });

      // Check if response contains binary data (image, audio, etc.)
      if (response.data.binaryData) {
        console.log('File upload binary data received:', {
          contentType: response.data.contentType,
          binaryDataLength: response.data.binaryData.length,
          isDataUrl: response.data.binaryData.startsWith('data:'),
          dataUrlStart: response.data.binaryData.substring(0, 50)
        });

        // Validate the data URL format
        if (!response.data.binaryData.startsWith('data:')) {
          console.error('Invalid data URL format in file upload:', response.data.binaryData.substring(0, 100));
          throw new Error('Invalid binary data format received from server');
        }

        // Try to convert data URL to blob URL for better performance
        let blobUrl = response.data.binaryData;
        try {
          // Extract base64 data from data URL
          const base64Data = response.data.binaryData.split(',')[1];
          const binaryData = atob(base64Data);
          const bytes = new Uint8Array(binaryData.length);
          for (let i = 0; i < binaryData.length; i++) {
            bytes[i] = binaryData.charCodeAt(i);
          }
          const blob = new Blob([bytes], { type: response.data.contentType });
          blobUrl = URL.createObjectURL(blob);
          console.log('Converted file upload data URL to blob URL:', blobUrl);

          // Note: We do NOT automatically revoke the URL here anymore.
          // It will be handled by the browser's page lifecycle or explicit cleanup if needed.
        } catch (error) {
          console.warn('Failed to convert file upload data URL to blob, using original data URL:', error.message);
        }

        return {
          success: true,
          response: response.data.reply,
          binaryData: blobUrl,
          contentType: response.data.contentType,
          data: response.data
        };
      }

      return {
        success: true,
        response: response.data.reply,
        data: response.data
      };
    } catch (error) {
      console.error('File upload error:', error);
      throw error;
    }
  }
};

// Knowledge base (RAG) API calls â€” admin-managed per user
export const knowledgeAPI = {
  list: async (userId) => {
    const res = await api.get(`/v2/admin/users/${userId}/kb`);
    return res.data.sources;
  },
  addText: async (userId, source, text) => {
    const res = await api.post(`/v2/admin/users/${userId}/kb/text`, { source, text });
    return res.data;
  },
  addUrl: async (userId, url) => {
    const res = await api.post(`/v2/admin/users/${userId}/kb/url`, { url });
    return res.data;
  },
  uploadFile: async (userId, formData) => {
    const res = await api.post(`/v2/admin/users/${userId}/kb/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return res.data;
  },
  deleteSource: async (userId, source) => {
    const res = await api.delete(`/v2/admin/users/${userId}/kb`, { data: { source } });
    return res.data;
  },
  // What would this question actually retrieve? Lets an admin fix a weak KB
  // instead of guessing from bad chat answers.
  search: async (userId, query) => {
    const res = await api.post(`/v2/admin/users/${userId}/kb/search`, { query });
    return res.data.hits;
  }
};

// Voice (text-to-speech) API calls
export const voiceAPI = {
  // Returns an object URL for the spoken audio (caller should revoke it)
  tts: async (text) => {
    const token = localStorage.getItem('token');
    const res = await fetch(`${API_BASE_URL}/voice/tts`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      },
      body: JSON.stringify({ text })
    });
    if (!res.ok) {
      let msg = 'Voice generation failed';
      try { msg = (await res.json()).message || msg; } catch { /* not json */ }
      throw new Error(msg);
    }
    const blob = await res.blob();
    return URL.createObjectURL(blob);
  }
};

// Conversation (chat history) API calls
export const conversationAPI = {
  list: async () => {
    const response = await api.get('/conversations');
    return response.data.conversations;
  },
  get: async (id) => {
    const response = await api.get(`/conversations/${id}`);
    return response.data.conversation;
  },
  create: async (title, messages) => {
    const response = await api.post('/conversations', { title, messages });
    return response.data.conversation;
  },
  save: async (id, messages) => {
    const response = await api.put(`/conversations/${id}`, { messages });
    return response.data.conversation;
  },
  rename: async (id, title) => {
    const response = await api.patch(`/conversations/${id}`, { title });
    return response.data.conversation;
  },
  remove: async (id, deletePassword) => {
    const response = await api.delete(`/conversations/${id}`, {
      headers: deletePassword ? { 'x-delete-password': deletePassword } : {}
    });
    return response.data;
  }
};

// Media API calls
export const mediaAPI = {
  // Upload media file
  uploadMedia: async (formData) => {
    try {
      const response = await api.post('/media/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        }
      });
      return response.data;
    } catch (error) {
      console.error('Media upload error:', error);
      throw error;
    }
  },

  // Upload media file with direct webhook forwarding using specific field names
  uploadDirectMedia: async (formData) => {
    try {
      const response = await api.post('/media/direct-upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        }
      });
      return response.data;
    } catch (error) {
      console.error('Direct media upload error:', error);
      throw error;
    }
  },

  // Get media file
  getMedia: async (fileId) => {
    try {
      const response = await api.get(`/media/files/${fileId}`);
      return response.data;
    } catch (error) {
      console.error('Media get error:', error);
      throw error;
    }
  },

  // List user's media files
  listMedia: async () => {
    try {
      const response = await api.get('/media/list');
      return response.data;
    } catch (error) {
      console.error('Media list error:', error);
      throw error;
    }
  },

  // Delete media file
  deleteMedia: async (fileId) => {
    try {
      const response = await api.delete(`/media/files/${fileId}`);
      return response.data;
    } catch (error) {
      console.error('Media delete error:', error);
      throw error;
    }
  }
};


export default api;
